import { useReducedMotion } from "@/hooks/useReducedMotion";
import { cn } from "@/lib/utils";
import { motion as designMotion } from "@/ui/tokens";
import { motion } from "framer-motion";
import React from "react";
import { Button, ButtonProps } from "./button";
import { appleSprings } from "@/ui/tokens/apple-springs";

/**
 * LiquidButton — Canonical Liquid Glass Button (iOS 26 Style)
 *
 * Implements Apple's Liquid Glass design language for buttons:
 * - Canonical material: backdrop-blur-lg + backdrop-saturate-[180%]
 * - Apple spring physics: scale 1.02 (hover), 0.98 (tap)
 * - Interactive ripple effect on click
 * - Reduced motion support
 *
 * @see CANON.md Section 5.4b — Liquid Glass System
 */

interface LiquidButtonProps extends ButtonProps {
  /** Button style variant */
  liquidVariant?: "flow" | "droplet" | "surface" | "telegram";
  /** Enable ripple effect on click */
  rippleEffect?: boolean;
}

const liquidVariants = {
  flow: [
    "bg-gradient-to-r from-primary via-primary-glow to-primary bg-size-200",
    "text-white font-semibold",
    "shadow-glow hover:shadow-float",
    "[&:hover]:text-white [&:active]:text-white [&:focus]:text-white",
    "border border-primary/20",
    "will-change-transform"
  ].join(" "),

  droplet: [
    "bg-gradient-to-r from-primary to-primary-glow",
    "text-white font-semibold border-primary/20",
    "shadow-glass hover:shadow-glow",
    "[&:hover]:text-white [&:active]:text-white [&:focus]:text-white",
    "will-change-transform"
  ].join(" "),

  surface: [
    "bg-gradient-to-r from-background to-secondary backdrop-blur-xl backdrop-saturate-[180%]",
    "text-foreground font-medium border-border/30",
    "hover:bg-gradient-to-r hover:from-primary hover:to-primary-glow",
    "hover:text-white [&:hover]:text-white [&:active]:text-white [&:focus]:text-white",
    "will-change-transform"
  ].join(" "),

  telegram: [
    "bg-gradient-to-r from-primary to-primary-glow telegram-stable",
    "text-white font-semibold border-primary/30",
    "hover:from-primary hover:to-primary-glow",
    "[&:hover]:text-white [&:active]:text-white [&:focus]:text-white",
    "shadow-[0_0_20px_rgb(59_130_246_/_0.25)] hover:shadow-[0_0_25px_rgb(59_130_246_/_0.35)]"
  ].join(" ")
};

export const LiquidButton = React.forwardRef<HTMLButtonElement, LiquidButtonProps>(
  ({ liquidVariant = "flow", rippleEffect = true, className, children, asChild, ...props }, ref) => {
    const [ripples, setRipples] = React.useState<Array<{ id: number; x: number; y: number }>>([]);
    const prefersReducedMotion = useReducedMotion();
    const timeoutRefs = React.useRef<Set<NodeJS.Timeout>>(new Set());

    // Cleanup timeouts on unmount to prevent memory leaks
    React.useEffect(() => {
      return () => {
        timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
        timeoutRefs.current.clear();
      };
    }, []);

    const handleClick = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
      if (rippleEffect && !prefersReducedMotion && liquidVariant !== "telegram") {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        const newRipple = { id: Date.now(), x, y };

        // Limit ripples to max 2 for better stability
        setRipples(prev => {
          const updated = [...prev.slice(-1), newRipple];
          return updated;
        });

        // Apple-style ripple: slower and larger (600ms instead of 400ms)
        const timeout = setTimeout(() => {
          setRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
          timeoutRefs.current.delete(timeout);
        }, 600);

        timeoutRefs.current.add(timeout);
      }

      if (props.onClick) {
        props.onClick(e);
      }
    }, [rippleEffect, prefersReducedMotion, props.onClick, liquidVariant]);

    // Apple-style spring physics with increased scale (more noticeable feedback)
    const motionProps = React.useMemo(() => {
      const isTelegram = liquidVariant === "telegram";
      if (prefersReducedMotion || isTelegram) {
        return {};
      }
      return {
        whileHover: { scale: 1.02 },  // Apple standard: 1.02-1.03 (was 1.005)
        whileTap: { scale: 0.98 },    // Apple standard: 0.98 (was 0.995)
        transition: appleSprings.scale
      };
    }, [prefersReducedMotion, liquidVariant]);

    // If asChild is true, we need to render differently to avoid multiple children
    if (asChild) {
      return (
        <motion.div
          {...motionProps}
          className="liquid-optimized"
        >
          <Button
            ref={ref}
            asChild
            className={cn(
              "relative overflow-hidden transition-all duration-240",
              // Canonical glass material: 12px blur + 180% saturation
              "border backdrop-blur-lg backdrop-saturate-[180%]",
              liquidVariants[liquidVariant],
              prefersReducedMotion ? "" : "hover:animate-liquid-morph",
              className
            )}
            onClick={handleClick}
            {...props}
          >
            {children}
          </Button>
        </motion.div>
      );
    }

    return (
      <motion.div
        {...motionProps}
        className="liquid-optimized"
      >
        <Button
          ref={ref}
          className={cn(
            "relative overflow-hidden transition-all duration-240 liquid-optimized",
            // Canonical glass material: 12px blur + 180% saturation
            "border backdrop-blur-lg backdrop-saturate-[180%]",
            liquidVariants[liquidVariant],
            // Simplified morphing - only for specific variants
            prefersReducedMotion || liquidVariant === "telegram" ? "" :
              liquidVariant === "flow" ? "hover:animate-liquid-flow-stable" : "",
            className
          )}
          onClick={handleClick}
          {...props}
        >
          {/* Enhanced contrast overlay for text visibility */}
          {!prefersReducedMotion && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {/* Text background for better contrast */}
              <div className="absolute inset-0 bg-black/5 dark:bg-white/5"></div>

              {/* Animated liquid drops effect */}
              <div className={cn("absolute top-2 left-4 w-1 h-1 bg-white/40 rounded-full delay-100", designMotion.animations.pulse)}></div>
              <div className={cn("absolute top-4 right-6 w-0.5 h-0.5 bg-white/30 rounded-full delay-300", designMotion.animations.pulse)}></div>
              <div className={cn("absolute bottom-3 left-8 w-1.5 h-1.5 bg-white/20 rounded-full delay-500", designMotion.animations.pulse)}></div>
              <div className={cn("absolute bottom-2 right-4 w-0.5 h-0.5 bg-white/25 rounded-full delay-700", designMotion.animations.pulse)}></div>

              {/* Flowing wave effect on hover */}
              <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-480">
                <div className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-transparent via-white/30 to-transparent transform translate-y-full hover:translate-y-0 transition-transform duration-700 ease-out"></div>
              </div>
            </div>
          )}

          {/* Ripple effects */}
          {ripples.map((ripple) => (
            <div
              key={ripple.id}
              className="absolute pointer-events-none"
              style={{
                left: `${ripple.x}%`,
                top: `${ripple.y}%`,
                transform: 'translate(-50%, -50%)'
              }}
            >
              <div className="w-2 h-2 bg-white/25 rounded-full animate-ping" />
            </div>
          ))}

          {/* Content with enhanced text contrast and shadow */}
          <span className="relative z-20 flex items-center justify-center space-x-2 font-semibold drop-shadow-sm">
            {children}
          </span>
        </Button>
      </motion.div>
    );
  }
);

LiquidButton.displayName = "LiquidButton";